import axios from 'axios';
import { TIMER_STATUS, WEEK_DAYS } from '../global/constants';
import { floatToHHMM, getAPIErrorReason, getDateString, getFirebaseUniqueUserId, isEmpty } from '../helpers/common';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import TimeTrackingCacheService from './timeTrackingCacheService';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { captureException } from './logService';
import { setTimerData, removeTimerData } from '../helpers/firebaseHelper';
import {
  CreateTimeLogDataInterface,
  TimeTrackingDataInterface,
  TrackedtimehistoryInterface,
  UpdateTimeLogDataInterface
} from '../interfaces/TimeTrackingInterface';
import MyTimeLogCacheService from './myTimeLogCacheService';
import { rootStore } from '../mobx/rootStore';
import { toJS } from 'mobx';
import { RawTimeTrackingDataInterface } from '../mobx/interfaces/task';
import { TimeLogInterface } from '../interfaces/TimeReportInterface';
import dayjs from 'dayjs';

interface TimeLogReportResponse {
  data: RawTimeTrackingDataInterface[];
  nextPage: string;
}

/**
 * @desc get Time Logs
 * @param {*}
 */
export const getTimeLogs = async (
  workspace_id: string,
  startingDate: string,
  endingDate: string,
  activeDate: string | undefined
) => {
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    if (!workspace_id) return false;
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const params = {
      user_id: userDetails?.id,
      company_id: workspace_id,
      start_date: getDateString(startingDate),
      end_date: getDateString(endingDate)
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/getWeekWiseTimeLogs`, {
      params
    });
    if (response) {
      const { data } = response;
      let dateRef = startingDate;
      let arrayRef: TimeTrackingDataInterface[] = [];
      let index = 0;
      WEEK_DAYS.forEach((day: string) => {
        let hours = 0;
        let formated_date = getDateString(dateRef) || '';
        const currentFormatedDate = Number(getDateString(new Date()));
        const logData = data.filter(
          (item: { formated_date: string; date: Date }) => dayjs(item?.date).format('YYYYMMDD') === formated_date
        );
        // sorting
        logData.sort(
          (
            a: { History: TrackedtimehistoryInterface[]; createdOn: string },
            b: { History: TrackedtimehistoryInterface[]; createdOn: string }
          ) => {
            // Get the earliest start time from history
            const aStartTime = a.History && a.History.length > 0 ? a.History[0].TimerStartedAt : a.createdOn;
            const bStartTime = b.History && b.History.length > 0 ? b.History[0].TimerStartedAt : b.createdOn;

            return new Date(aStartTime).getTime() - new Date(bStartTime).getTime();
          }
        );
        logData?.forEach((item: { hours: number }) => {
          hours = hours + item?.hours;
        });
        let dayObject = {
          index,
          day,
          date: dateRef,
          formated_date,
          logData,
          hours,
          active: Number(formated_date) <= currentFormatedDate
        };
        if (formated_date === activeDate) {
          rootStore.timeTrackingStore.setCurrentTimeTracking(dayObject);
        }
        arrayRef.push(dayObject);
        dateRef = dayjs(new Date(dateRef)).add(1, 'day').toLocaleString();
        index = index + 1;
      });
      // Update MobX store
      rootStore.timeTrackingStore.setTimeTackingData(arrayRef);
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get time tracking list please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
};

/**
 * @desc get time tracking
 * @param {*}
 */
export const getActiveTime = async () => {
  try {
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/getActiveTimelogs`);
    const { data } = response;
    if (data) {
      if (data?.length === 0) {
        rootStore.timeTrackingStore.setCurrentStartTime({ task_id: undefined, time: 0 });
      }
      rootStore.timeTrackingStore.setActiveTimeLogs(data || []);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get active time tracking please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
  }
};

/* @desc add time log data
 * @param {*}
 */
export const addTimeLogDetails = async (payload: CreateTimeLogDataInterface) => {
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    if (!payload.project_id) {
      dispatchTimeTrackingError('Project Id is required for add timelog');
      return;
    }
    if (!payload.task_id) {
      dispatchTimeTrackingError('Task Id is required for add timelog');
      return;
    }
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/addTimeLog`, payload);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add time log data please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
};

/* @desc update time log data
 * @param {*}
 */
export const updateTimeLogDetails = async (itemId: string, payload: UpdateTimeLogDataInterface) => {
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    if (!itemId) {
      const error = 'Item Id is required for update timelog';
      captureException(error);
      dispatchTimeTrackingError(error);
      return false;
    }
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/putTimelogForTracker/${itemId}`, payload);
    if (response) {
      return response;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update time log data please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return undefined;
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
};

/* @desc delete/archive time log data
 * @param {*}
 */
export const archiveTimeLog = async (timelogId: string) => {
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/archiveTimelogForTracker/${timelogId}`);
    if (response) {
      rootStore.alertNotificationStore.setSuccessMessage('Time log deleted successfully!');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete time log please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
};

/* @desc load Edit Details
 * @param {*}
 */
export const loadEditTimeLog = async () => {
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    const input = rootStore.timeTrackingStore.timeEntryInput || {};
    const projectList = rootStore.projectStore.projects?.map((item) => {
      return { label: item?.name, id: item?.id, value: item?.id };
    });
    if (input?.id) {
      const project = projectList?.find((item: { id: string }) => item?.id === input?.projectId);
      //get project group list
      const tasks = rootStore.projectStore.projects?.find((item) => item?.id === project?.id)?.tasks;
      const taskList = tasks?.map((item) => {
        return { id: item?.id, value: item?.id, label: item?.name };
      });
      const task = taskList?.find((item: { id: string }) => item?.id === input?.taskId);
      // Extract start time from createdOn or History if available
      let startTime = '00:00';
      if (input?.trackingStartTime || input?.createdOn) {
        startTime = input.trackingStartTime
          ? dayjs(input.trackingStartTime).format('HH:mm')
          : dayjs(input.createdOn).format('HH:mm');
      } else if (input?.History && input.History.length > 0 && input.History[0]?.TimerStartedAt) {
        startTime = dayjs(input.History[0].TimerStartedAt).format('HH:mm');
      }
      const updatedInput = {
        ...input,
        projectId: project,
        // taskGroupId: taskGroup,
        taskId: task,
        hours: floatToHHMM(Number(input?.hours)),
        startTime: startTime
      };
      rootStore.timeTrackingStore.setTimeEntryInput(updatedInput);
      rootStore.timeTrackingStore.setListDropdown({ projectList, taskList });
    } else {
      rootStore.timeTrackingStore.updateListDropdown({ propsName: 'projectList', value: projectList });
    }
  } catch (e) {
    const error = 'Unable to load edit time log data please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
};

const updateTimeTrackingReportData = (data: RawTimeTrackingDataInterface[]) => {
  const updatedData = data.map((item) => {
    const taskData = rootStore.taskStore.tasks?.find((task) => task?.id === item?.taskId);
    const userData = rootStore.workspaceStore.currentWorkspace?.users?.find((user) => user?.id === item?.createdBy);
    return {
      ...item,
      taskName: taskData?.name || item.taskName,
      userName: userData?.name || item.userName,
      projectName: taskData?.project?.name || item.projectName,
      labels: taskData?.labels?.map((label) => toJS(label)) || []
    };
  });
  return updatedData;
};

export const loadTimeLogs = async (
  workspace_id: string | undefined,
  months: string[]
): Promise<RawTimeTrackingDataInterface[]> => {
  if (!workspace_id) {
    return [];
  }
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    if (!months || months?.length === 0) {
      const today = new Date();
      const lastMonths = [];
      for (let i = 0; i < 3; i++) {
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const year = today.getFullYear();
        lastMonths.push(`${month}-${year}`);
        today.setMonth(today.getMonth() - 1);
      }
      months = lastMonths;
    }

    const local_data = await TimeTrackingCacheService.getInstance()?.getTimeTrackingByWorkspace();
    if (local_data && local_data.length > 0) {
      const updatedData = updateTimeTrackingReportData(local_data);
      rootStore.reportStore.setTimeTrackingReportData(updatedData);
    }

    let lastUpdatedTime = await TimeTrackingCacheService.getInstance()?.getLastUpdatedTimeMonthWise(workspace_id);

    let updateTimeArray = lastUpdatedTime && lastUpdatedTime !== 0 ? JSON.parse(JSON.stringify(lastUpdatedTime)) : {};
    const currentTime = dayjs(); // Current time
    const adjustedTime = currentTime.subtract(30, 'minute').toISOString();

    let hasUpdatedData = false;
    await Promise.all(
      months.map(async (month: string) => {
        updateTimeArray[month] = adjustedTime;
        const monthLastUpdatedTime = lastUpdatedTime?.[month];
        const params = {
          date: month,
          lastUpdatedTime: monthLastUpdatedTime
        };

        let next_page_value = '';
        do {
          const response = await axios.get<TimeLogReportResponse>(
            `${REACT_APP_TEAMCAMP_APIURL}/timeLog/getTimeLogReport${next_page_value}`,
            { params }
          );
          const { data, nextPage } = response.data;
          if (lastUpdatedTime === 0 || isEmpty(lastUpdatedTime)) {
            await TimeTrackingCacheService.getInstance()?.setTimeTrackingData(data, workspace_id);
            hasUpdatedData = true;
          } else {
            // await TimeTrackingCacheService.getInstance()?.updateTimeLogList(data, [], workspace_id);
            const updatedData = data?.filter((item: RawTimeTrackingDataInterface) => !item?.isArchive);
            const archivedData = data
              ?.filter((item: RawTimeTrackingDataInterface) => item?.isArchive)
              ?.map((item: RawTimeTrackingDataInterface) => item?.id);
            await TimeTrackingCacheService.getInstance()?.updateTimeLogList(updatedData, archivedData, workspace_id);
            if (data && data.length > 0) {
              hasUpdatedData = true;
            }
          }
          next_page_value = nextPage;
        } while (next_page_value);
      })
    );

    if (hasUpdatedData) {
      await TimeTrackingCacheService.getInstance()?.setLastUpdatedTimeMonthWise(updateTimeArray, workspace_id);
      const local_updated_data = await TimeTrackingCacheService.getInstance()?.getTimeTrackingByWorkspace();
      if (local_updated_data && local_updated_data.length > 0) {
        const updatedData = updateTimeTrackingReportData(local_updated_data);
        rootStore.reportStore.setTimeTrackingReportData(updatedData);
        return updatedData;
      }
    } else {
      const local_updated_data = await TimeTrackingCacheService.getInstance()?.getTimeTrackingByWorkspace();
      const updatedData = updateTimeTrackingReportData(local_updated_data || []);
      rootStore.reportStore.setTimeTrackingReportData(updatedData);
      return updatedData;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to load time log data please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return [];
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
  return [];
};
export const loadMyTimeLog = async (workspace_id: string, months: string[]) => {
  try {
    rootStore.timeTrackingStore.setTimeTrackingLoader(true);
    if (!months || months?.length === 0) {
      const today = new Date();
      const lastMonths = [];
      for (let i = 0; i < 3; i++) {
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const year = today.getFullYear();
        lastMonths.push(`${month}-${year}`);
        today.setMonth(today.getMonth() - 1);
      }
      months = lastMonths;
    }

    const local_data = await MyTimeLogCacheService.getInstance()?.getMyTimeLogByWorkspace();
    if (local_data && local_data.length > 0) {
      const updatedData = updateTimeTrackingReportData(local_data);
      rootStore.timeTrackingStore.setMyTimeLog(updatedData as unknown as TimeLogInterface[]);
    }

    let lastUpdatedTime = await MyTimeLogCacheService.getInstance()?.getLastUpdatedTimeMonthWise(workspace_id);

    let updateTimeArray = lastUpdatedTime && lastUpdatedTime !== 0 ? JSON.parse(JSON.stringify(lastUpdatedTime)) : {};
    const currentTime = dayjs(); // Current time
    const adjustedTime = currentTime.subtract(30, 'minute').toISOString();

    let hasUpdatedData = false;
    await Promise.all(
      months.map(async (month: string) => {
        updateTimeArray[month] = adjustedTime;
        const monthLastUpdatedTime = lastUpdatedTime?.[month];
        const params = {
          date: month,
          lastUpdatedTime: monthLastUpdatedTime
        };

        let next_page_value = '';
        do {
          const response = await axios.get<TimeLogReportResponse>(
            `${REACT_APP_TEAMCAMP_APIURL}/timeLog/myTimeLogsReport${next_page_value}`,
            { params }
          );
          const { data, nextPage } = response.data;

          if (lastUpdatedTime === 0 || isEmpty(lastUpdatedTime)) {
            await MyTimeLogCacheService.getInstance()?.setMyTimeLogData(data, workspace_id);
            hasUpdatedData = true;
          } else {
            const updatedData = data?.filter((item: RawTimeTrackingDataInterface) => !item?.isArchive);
            const archivedData = data
              ?.filter((item: RawTimeTrackingDataInterface) => item?.isArchive)
              ?.map((item: RawTimeTrackingDataInterface) => item?.id);
            await MyTimeLogCacheService.getInstance()?.updateTimeLogList(updatedData, archivedData, workspace_id);
            if (data && data.length > 0) {
              hasUpdatedData = true;
            }
          }
          next_page_value = nextPage;
        } while (next_page_value);
      })
    );

    if (hasUpdatedData) {
      await MyTimeLogCacheService.getInstance()?.setLastUpdatedTimeMonthWise(updateTimeArray, workspace_id);
      const local_updated_data = await MyTimeLogCacheService.getInstance()?.getMyTimeLogByWorkspace();
      if (local_updated_data && local_updated_data.length > 0) {
        const updatedData = updateTimeTrackingReportData(local_updated_data);
        rootStore.timeTrackingStore.setMyTimeLog(updatedData as unknown as TimeLogInterface[]);
        return updatedData;
      }
    } else {
      const local_updated_data = await MyTimeLogCacheService.getInstance()?.getMyTimeLogByWorkspace();
      const updatedData = updateTimeTrackingReportData(local_updated_data || []);
      rootStore.timeTrackingStore.setMyTimeLog(updatedData as unknown as TimeLogInterface[]);
      return updatedData;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to load my time log data please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
  } finally {
    rootStore.timeTrackingStore.setTimeTrackingLoader(false);
  }
};
/**
 * @desc get time tracking
 * @param {*}
 */
export const startTracker = async (taskId: string | undefined) => {
  try {
    if (!taskId) return;
    const uniqueId = getFirebaseUniqueUserId();
    if (!uniqueId.userId || !uniqueId.workspaceId) return;
    const success = await setTimerData(
      uniqueId?.uniqueUserId,
      taskId,
      uniqueId?.userId,
      uniqueId?.workspaceId,
      TIMER_STATUS?.START.toString()
    );

    if (!success) {
      throw new Error('Failed to start timer');
    }

    return true;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update time tracking when start tracker please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
  }
};

/**
 * @desc get time tracking
 * @param {*}
 */
export const stopTracker = async () => {
  try {
    const uniqueId = getFirebaseUniqueUserId();
    const success = await removeTimerData(uniqueId?.uniqueUserId);

    if (!success) {
      throw new Error('Failed to stop timer');
    }

    return true;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update time tracking when stop tracker please try again';
    captureException(error);
    dispatchTimeTrackingError(error);
    return false;
  } finally {
  }
};

function dispatchTimeTrackingError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}
